import {
    Card,
    Divider,
    Header,
    Inline,
    ListBox,
    ListBoxItem,
    ListBoxSection,
    PopoverBase,
    SearchField,
    Span,
    Spinner,
    Stack,
    Text
} from "@hopper-ui/components";
import { ArrowRightIcon, IconContext, SparklesIcon } from "@hopper-ui/icons";
import { Shortcut, textEllipsisProps } from "@sg-protect/components";
import { FeatureFlagKeys } from "@sg-protect/federation-runtime";
import { useFeatureFlag } from "@sg-protect/federation-runtime/squide";
import { type ReactNode, useCallback, useContext, useMemo } from "react";
import {
    Autocomplete,
    AutocompleteStateContext,
    type Key,
    type KeyboardEvent,
    type Selection,
    useSlottedContext
} from "react-aria-components";
import { useNavigate } from "react-router";
import { type ShellTFunction, useShellTranslation } from "../../../i18n/i18next.ts";
import { useTrackLinkClicked } from "../../../tracking/useTrackEvent.ts";
import { type GenerateAiReportLocationState, useTenantAssessmentPaths } from "../../../useTenantAssessmentPaths.ts";
import styles from "./CommandPalettePopover.module.css";
import { COMMAND_PALETTE_WIDTH } from "./CommandPaletteTrigger.tsx";
import {
    type ProtectRecentlyViewedEntry,
    type ProtectSearchProviderOptions,
    type ProtectSearchResult,
    SearchProviderBoundary
} from "./registerProtectSearchProvider.tsx";
import type { DebouncedSearchQuery } from "./useDebouncedSearchQuery.ts";
import { useRecentlyViewed } from "./useRecentlyViewed.ts";

const RESULTS_PER_GROUP = 5;

// The shell owns the sentences, the providers own the nouns in them.
// Hand-rolled rather than Intl.ListFormat, which adds an Oxford comma this product's copy does not use.
function formatLabels(labels: string[], lastSeparator: "and" | "or", t: ShellTFunction) {
    if (labels.length < 2) {
        return labels[0] ?? "";
    }

    const values = { items: labels.slice(0, -1).join(", "), last: labels[labels.length - 1] };

    return lastSeparator === "and" ? t("CommandPalette.list.and", values) : t("CommandPalette.list.or", values);
}

const GENERATE_AI_REPORT_KEY = "action:generate-ai-report";

const GENERATE_AI_REPORT_TRIGGER = "Generate report with AI";

const NO_SELECTION: Selection = new Set<string>();

const NO_ENTRIES: ProtectRecentlyViewedEntry[] = [];

const NO_RESULTS: ProtectSearchResult[] = [];

const TRACKING_LOCATION = "Global Search Bar";

export interface CommandPalettePopoverProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    search: DebouncedSearchQuery;
    /** Must keep the same length for the palette's lifetime: one hook runs per entry. */
    providers: ProtectSearchProviderOptions[];
}

interface SearchStatusProps {
    message: string;
    isLoading: boolean;
}

interface CommandPaletteDestination {
    path: string;
    trigger: string;
}

interface ProviderSection {
    id: string;
    sectionLabel: ReactNode;
    rows: ProtectSearchResult[];
}

function SearchStatus({ message, isLoading }: SearchStatusProps) {
    const { t } = useShellTranslation();

    return (
        <Inline alignY="center" gap="inline-sm" paddingX="inset-lg" paddingY="inset-sm">
            {isLoading && <Spinner size="sm" aria-label={t("CommandPalette.status.searching")} />}
            <Text size="sm" color="neutral-weak" data-public>
                {message}
            </Text>
        </Inline>
    );
}

function getSearchLabel(entityLabels: string[], t: ShellTFunction) {
    return entityLabels.length > 0
        ? t("CommandPalette.searchEntities", { entities: formatLabels(entityLabels, "or", t) })
        : t("CommandPalette.search");
}

function getStatus(
    isBelowMinLength: boolean,
    isSearching: boolean,
    entityLabels: string[],
    t: ShellTFunction
): SearchStatusProps {
    if (isBelowMinLength) {
        return {
            message: t("CommandPalette.status.typeToSearch", { entities: formatLabels(entityLabels, "and", t) }),
            isLoading: false
        };
    }

    if (isSearching) {
        return { message: t("CommandPalette.status.searchingEllipsis"), isLoading: true };
    }

    return {
        message: t("CommandPalette.status.noResults", { entities: formatLabels(entityLabels, "or", t) }),
        isLoading: false
    };
}

function getTriggerTopEdge(target: Element) {
    const rect = target.getBoundingClientRect();

    return new DOMRect(rect.x, rect.y, rect.width, 0);
}

function resultKey(providerId: string, resultId: string) {
    return `${providerId}:${resultId}`;
}

function useFocusedNodeId() {
    return useContext(AutocompleteStateContext)?.focusedNodeId;
}

interface CommandPaletteSearchFieldProps {
    searchLabel: string;
    onSubmitFirstResult: () => void;
    onDismiss: () => void;
}

function CommandPaletteSearchField({ searchLabel, onSubmitFirstResult, onDismiss }: CommandPaletteSearchFieldProps) {
    const isOptionVirtuallyFocused = useFocusedNodeId() != null;

    const handleSubmit = useCallback(() => {
        if (!isOptionVirtuallyFocused) {
            onSubmitFirstResult();
        }
    }, [isOptionVirtuallyFocused, onSubmitFirstResult]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape" && !event.nativeEvent.isComposing) {
                onDismiss();
            }
        },
        [onDismiss]
    );

    return (
        <Inline alignY="center" gap="inline-sm" marginX="inline-sm" marginTop="stack-sm">
            <SearchField
                autoFocus
                flexGrow={1}
                width="auto"
                size="sm"
                aria-label={searchLabel}
                placeholder={searchLabel}
                inputGroupProps={{ border: "none", backgroundColor: "transparent" }}
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
            />
            <Shortcut keys="Escape" />
        </Inline>
    );
}

interface FirstResultShortcutProps {
    isItemFocused: boolean;
}

function FirstResultShortcut({ isItemFocused }: FirstResultShortcutProps) {
    const focusedNodeId = useFocusedNodeId();
    const endIconProps = useSlottedContext(IconContext, "end-icon");

    if (focusedNodeId != null && !isItemFocused) {
        return null;
    }

    return <Shortcut keys="Enter" className={endIconProps?.className} gridRow="1 / -1" alignSelf="center" />;
}

interface SearchResultProps {
    id: string;
    result: ProtectSearchResult;
    isFirstResult: boolean;
}

function SearchResult({ id, result, isFirstResult }: SearchResultProps) {
    return (
        <ListBoxItem id={id} textValue={result.displayName}>
            {({ isFocused }) => (
                <>
                    {result.icon}
                    <Text>{result.displayName}</Text>
                    {result.description !== undefined && <Text slot="description">{result.description}</Text>}
                    {isFirstResult && <FirstResultShortcut isItemFocused={isFocused} />}
                </>
            )}
        </ListBoxItem>
    );
}

interface GenerateAiReportResultProps {
    isEnterTarget: boolean;
    query: string;
}

function GenerateAiReportResult({ isEnterTarget, query }: GenerateAiReportResultProps) {
    const { t } = useShellTranslation();

    return (
        <ListBoxItem id={GENERATE_AI_REPORT_KEY} textValue={t("CommandPalette.generateAiReport.label")}>
            {({ isFocused }) => (
                <>
                    <SparklesIcon />
                    <Text {...textEllipsisProps}>
                        <Span data-public>
                            {query
                                ? t("CommandPalette.generateAiReport.withPrompt")
                                : t("CommandPalette.generateAiReport.label")}
                        </Span>
                        {query && (
                            <>
                                {" "}
                                <Span fontWeight="body-md-semibold">{query}</Span>
                            </>
                        )}
                    </Text>
                    {isEnterTarget ? (
                        <FirstResultShortcut isItemFocused={isFocused} />
                    ) : (
                        <ArrowRightIcon slot="end-icon" />
                    )}
                </>
            )}
        </ListBoxItem>
    );
}

function PalettePopover({ isOpen, onOpenChange, search, providers }: CommandPalettePopoverProps) {
    const { t } = useShellTranslation();
    const { query, debouncedQuery, setQuery, setQueryNow, reset } = search;
    const paths = useTenantAssessmentPaths();
    const navigate = useNavigate();
    const { entriesByProviderId } = useRecentlyViewed();
    const trackLinkClicked = useTrackLinkClicked();
    const isAiFeaturesEnabled = useFeatureFlag(FeatureFlagKeys.AiFeaturesEnabled, true);

    const trimmedQuery = query.trim();
    const trimmedDebouncedQuery = debouncedQuery.trim();

    // One hook call per provider, in registry order: safe because `providers` never changes while the palette is mounted.
    const providerStates = providers.map(provider => {
        const isEnabled = isOpen && trimmedDebouncedQuery.length >= provider.minQueryLength;

        return provider.useResults(trimmedDebouncedQuery, isEnabled);
    });
    const recentRowsByProvider = providers.map(
        provider => provider.useRecentResults?.(entriesByProviderId.get(provider.id) ?? NO_ENTRIES) ?? NO_RESULTS
    );

    const hasProviders = providers.length > 0;
    const entityLabels = providers.flatMap(provider => provider.entityLabels);
    const searchLabel = getSearchLabel(entityLabels, t);

    // No providers means nothing can ever match, so stay below the minimum rather than faking a search.
    const minQueryLength = hasProviders
        ? Math.min(...providers.map(provider => provider.minQueryLength))
        : Number.POSITIVE_INFINITY;
    const isBelowMinLength = trimmedQuery.length < minQueryLength;
    const isDebouncePending = !isBelowMinLength && trimmedDebouncedQuery !== trimmedQuery;

    // Judged on the typed query, not the debounced one: during the debounce every provider is still disabled.
    const hasSettledResults =
        !isBelowMinLength &&
        providers.every((provider, index) => {
            const { results, isError } = providerStates[index];

            return trimmedQuery.length < provider.minQueryLength || results !== undefined || isError;
        });
    const isSearching = isDebouncePending || (providerStates.some(state => state.isFetching) && !hasSettledResults);

    const showRecent = trimmedQuery.length === 0 && recentRowsByProvider.some(rows => rows.length > 0);

    const sections = providers.map<ProviderSection>((provider, index) => {
        if (showRecent) {
            return { id: provider.id, sectionLabel: provider.recentSectionLabel, rows: recentRowsByProvider[index] };
        }

        return {
            id: provider.id,
            sectionLabel: provider.sectionLabel,
            rows: hasSettledResults ? (providerStates[index].results ?? NO_RESULTS).slice(0, RESULTS_PER_GROUP) : []
        };
    });

    const warnings = providers.flatMap((provider, index) => {
        const { warning } = providerStates[index];

        return warning ? [{ id: provider.id, warning }] : [];
    });

    const hasRows = sections.some(section => section.rows.length > 0);
    const showGenerateAiReport = isAiFeaturesEnabled && (isBelowMinLength || hasSettledResults);
    const generateAiReportPrompt = isBelowMinLength ? "" : trimmedQuery;
    const status = hasRows || !hasProviders ? undefined : getStatus(isBelowMinLength, isSearching, entityLabels, t);

    const destinationByKey = useMemo(() => {
        const entries = new Map<Key, CommandPaletteDestination>();

        sections.forEach(section => {
            section.rows.forEach(row => {
                entries.set(resultKey(section.id, row.id), { path: row.to, trigger: row.trackingTrigger });
            });
        });

        return entries;
    }, [sections]);

    const firstResultKey: Key | undefined = destinationByKey.keys().next().value;

    const closeAfterNavigation = useCallback(() => {
        reset();
        onOpenChange(false);
    }, [onOpenChange, reset]);

    const handleDismiss = useCallback(() => {
        setQueryNow(query);
        onOpenChange(false);
    }, [onOpenChange, query, setQueryNow]);

    const navigateToGenerateAiReport = useCallback(() => {
        trackLinkClicked(GENERATE_AI_REPORT_TRIGGER, TRACKING_LOCATION);
        void navigate(paths.reportsGenerate, {
            state: { prompt: generateAiReportPrompt } satisfies GenerateAiReportLocationState
        });
        closeAfterNavigation();
    }, [navigate, paths, generateAiReportPrompt, trackLinkClicked, closeAfterNavigation]);

    const navigateToKey = useCallback(
        (key: Key) => {
            if (key === GENERATE_AI_REPORT_KEY) {
                navigateToGenerateAiReport();

                return;
            }

            const destination = destinationByKey.get(key);

            if (destination !== undefined) {
                trackLinkClicked(destination.trigger, TRACKING_LOCATION);
                void navigate(destination.path);
                closeAfterNavigation();
            }
        },
        [navigate, destinationByKey, closeAfterNavigation, navigateToGenerateAiReport, trackLinkClicked]
    );

    const handleSubmitFirstResult = useCallback(() => {
        if (isDebouncePending) {
            return;
        }

        if (firstResultKey !== undefined) {
            navigateToKey(firstResultKey);
        } else if (showGenerateAiReport) {
            navigateToGenerateAiReport();
        }
    }, [isDebouncePending, firstResultKey, navigateToKey, showGenerateAiReport, navigateToGenerateAiReport]);

    return (
        <PopoverBase
            className={styles.popover}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            placement="bottom"
            offset={0}
            shouldFlip={false}
            getTargetRect={getTriggerTopEdge}
            aria-label={t("CommandPalette.search")}
            backgroundColor="transparent"
            border="none"
            boxShadow="none"
            alignItems="stretch"
            UNSAFE_width={COMMAND_PALETTE_WIDTH}
        >
            <Card
                className={styles.card}
                elevation="elevated"
                padding="core_0"
                overflow="hidden"
                borderRadius="rounded-md"
            >
                <Autocomplete inputValue={query} onInputChange={setQuery} filter={() => true} disableAutoFocusFirst>
                    <Stack gap="stack-sm" alignX="stretch" paddingBottom="inset-sm">
                        <CommandPaletteSearchField
                            searchLabel={searchLabel}
                            onSubmitFirstResult={handleSubmitFirstResult}
                            onDismiss={handleDismiss}
                        />
                        <Divider />
                        {status && <SearchStatus {...status} />}
                        {(hasRows || showGenerateAiReport) && (
                            <>
                                {status && <Divider />}
                                <ListBox
                                    className={styles.results}
                                    aria-label={t("CommandPalette.results")}
                                    isFluid
                                    border="none"
                                    paddingY="core_0"
                                    selectionMode="single"
                                    selectedKeys={NO_SELECTION}
                                    onAction={navigateToKey}
                                >
                                    {sections.flatMap(section =>
                                        section.rows.length > 0
                                            ? [
                                                  <ListBoxSection key={section.id}>
                                                      <Header>{section.sectionLabel}</Header>
                                                      {section.rows.map(row => {
                                                          const key = resultKey(section.id, row.id);

                                                          return (
                                                              <SearchResult
                                                                  key={key}
                                                                  id={key}
                                                                  result={row}
                                                                  isFirstResult={key === firstResultKey}
                                                              />
                                                          );
                                                      })}
                                                  </ListBoxSection>
                                              ]
                                            : []
                                    )}
                                    {showGenerateAiReport && (
                                        <ListBoxSection aria-label={t("CommandPalette.aiSection")}>
                                            <GenerateAiReportResult
                                                isEnterTarget={firstResultKey === undefined}
                                                query={generateAiReportPrompt}
                                            />
                                        </ListBoxSection>
                                    )}
                                </ListBox>
                            </>
                        )}
                        {warnings.length > 0 && (
                            <>
                                <Divider />
                                {warnings.map(({ id, warning }) => (
                                    <Text key={id} size="xs" color="neutral-weak" paddingX="inset-lg">
                                        {warning}
                                    </Text>
                                ))}
                            </>
                        )}
                    </Stack>
                </Autocomplete>
            </Card>
        </PopoverBase>
    );
}

// The provider hooks run in PalettePopover, so the boundary sits above it: react-aria rejects one between ListBox and its sections.
export function CommandPalettePopover(props: CommandPalettePopoverProps) {
    const { isOpen, onOpenChange, search } = props;

    // Close so the trigger and Mod+K work again, and drop the query in case it is what made the provider throw.
    const handleError = useCallback(() => {
        search.reset();
        onOpenChange(false);
    }, [onOpenChange, search]);

    return (
        <SearchProviderBoundary resetKeys={[isOpen]} onError={handleError}>
            <PalettePopover {...props} />
        </SearchProviderBoundary>
    );
}
